// ============================================================
// FILE: src/api/services.js
// PURPOSE: Every API call in one place.
//          Components just call these functions — no raw axios.
// ============================================================

import api from './axios';

// ── AUTH ──────────────────────────────────────────────────
export const authAPI = {
  login:          (data) => api.post('/auth/login', data),
  getMe:          ()     => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// ── DASHBOARD ─────────────────────────────────────────────
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// ── CLIENTS ───────────────────────────────────────────────
export const clientsAPI = {
  getAll:    ()         => api.get('/clients'),
  getById:   (id)       => api.get(`/clients/${id}`),
  search:    (q)        => api.get(`/clients/search?q=${q}`),
  create:    (data)     => api.post('/clients', data),
  update:    (id, data) => api.put(`/clients/${id}`, data),
  delete:    (id)       => api.delete(`/clients/${id}`),
};

// ── CAREGIVERS ────────────────────────────────────────────
export const caregiversAPI = {
  getAll:        ()         => api.get('/caregivers'),
  getById:       (id)       => api.get(`/caregivers/${id}`),
  create:        (data)     => api.post('/caregivers', data),
  update:        (id, data) => api.put(`/caregivers/${id}`, data),
  delete:        (id)       => api.delete(`/caregivers/${id}`),
  toggleStatus:  (id)       => api.patch(`/caregivers/${id}/toggle-status`),
};

// ── VISITS ────────────────────────────────────────────────
export const visitsAPI = {
  getAll:          ()         => api.get('/visits'),
  getById:         (id)       => api.get(`/visits/${id}`),
  getToday:        ()         => api.get('/visits/today'),
  getByClient:     (id)       => api.get(`/visits/client/${id}`),
  getByCaregiver:  (id)       => api.get(`/visits/caregiver/${id}`),
  create:          (data)     => api.post('/visits', data),
  update:          (id, data) => api.put(`/visits/${id}`, data),
  updateStatus:    (id, data) => api.patch(`/visits/${id}/status`, data),
  delete:          (id)       => api.delete(`/visits/${id}`),
};

// ── ATTENDANCE ────────────────────────────────────────────
export const attendanceAPI = {
  getAll:           ()         => api.get('/attendance'),
  getByCaregiver:   (id)       => api.get(`/attendance/caregiver/${id}`),
  getByClient:      (id)       => api.get(`/attendance/client/${id}`),
  getActive:        ()         => api.get('/attendance/active'),
  clockIn:          (data)     => api.post('/attendance/clockin', data),
  clockOut:         (id, data) => api.put(`/attendance/clockout/${id}`, data),
  createManual:     (data)     => api.post('/attendance/manual', data),
  update:           (id, data) => api.put(`/attendance/${id}`, data),
  delete:           (id)       => api.delete(`/attendance/${id}`),
};

// ── BILLING ───────────────────────────────────────────────
export const billingAPI = {
  getAll:          ()         => api.get('/billing'),
  getById:         (id)       => api.get(`/billing/${id}`),
  getByClient:     (id)       => api.get(`/billing/client/${id}`),
  getSummary:      ()         => api.get('/billing/summary'),
  create:          (data)     => api.post('/billing', data),
  update:          (id, data) => api.put(`/billing/${id}`, data),
  updateStatus:    (id, data) => api.patch(`/billing/${id}/status`, data),
  delete:          (id)       => api.delete(`/billing/${id}`),
};

// ── PAYROLL ───────────────────────────────────────────────
export const payrollAPI = {
  getAll:         ()         => api.get('/payroll'),
  getById:        (id)       => api.get(`/payroll/${id}`),
  getByCaregiver: (id)       => api.get(`/payroll/caregiver/${id}`),
  calculate:      (data)     => api.post('/payroll/calculate', data),
  create:         (data)     => api.post('/payroll', data),
  update:         (id, data) => api.put(`/payroll/${id}`, data),
  delete:         (id)       => api.delete(`/payroll/${id}`),
};

// ── TRAINING ──────────────────────────────────────────────
export const trainingAPI = {
  getAll:         ()         => api.get('/training'),
  getById:        (id)       => api.get(`/training/${id}`),
  getByCaregiver: (id)       => api.get(`/training/caregiver/${id}`),
  getExpiring:    (days)     => api.get(`/training/expiring?days=${days || 30}`),
  create:         (data)     => api.post('/training', data),
  update:         (id, data) => api.put(`/training/${id}`, data),
  delete:         (id)       => api.delete(`/training/${id}`),
};

// ── DOCUMENTS ─────────────────────────────────────────────
export const documentsAPI = {
  getAll:       ()     => api.get('/documents'),
  getByClient:  (id)   => api.get(`/documents/client/${id}`),
  getById:      (id)   => api.get(`/documents/${id}`),
  upload:       (data) => api.post('/documents/upload', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete:       (id)   => api.delete(`/documents/${id}`),
};

// ── NOTIFICATIONS ─────────────────────────────────────────
export const notificationsAPI = {
  getMine:       ()   => api.get('/notifications'),
  getUnreadCount:()   => api.get('/notifications/unread-count'),
  markRead:      (id) => api.put(`/notifications/${id}/read`),
  markAllRead:   ()   => api.put('/notifications/mark-all-read'),
  delete:        (id) => api.delete(`/notifications/${id}`),
};

// ── MEDICAID ──────────────────────────────────────────────
export const medicaidAPI = {
  getAll:       ()                      => api.get('/medicaid'),
  getByClient:  (id)                    => api.get(`/medicaid/client/${id}`),
  getById:      (id)                    => api.get(`/medicaid/${id}`),
  getHoursUsed: (clientId, month, year) => api.get(`/medicaid/hours-used/${clientId}/${month}/${year}`),
  create:       (data)                  => api.post('/medicaid', data),
  update:       (id, data)              => api.put(`/medicaid/${id}`, data),
  delete:       (id)                    => api.delete(`/medicaid/${id}`),
};

// ── CARE PLANS ────────────────────────────────────────────
export const carePlansAPI = {
  getAll:      ()         => api.get('/careplans'),
  getById:     (id)       => api.get(`/careplans/${id}`),
  getByClient: (id)       => api.get(`/careplans/client/${id}`),
  create:      (data)     => api.post('/careplans', data),
  update:      (id, data) => api.put(`/careplans/${id}`, data),
  delete:      (id)       => api.delete(`/careplans/${id}`),
};

// ── USERS ─────────────────────────────────────────────────
export const usersAPI = {
  getAll:         ()         => api.get('/users'),
  getById:        (id)       => api.get(`/users/${id}`),
  create:         (data)     => api.post('/users', data),
  update:         (id, data) => api.put(`/users/${id}`, data),
  toggleStatus:   (id)       => api.patch(`/users/${id}/toggle-status`),
  resetPassword:  (id, data) => api.patch(`/users/${id}/reset-password`, data),
  delete:         (id)       => api.delete(`/users/${id}`),
};

// ── SERVICES ──────────────────────────────────────────────
export const servicesAPI = {
  getAll:   ()         => api.get('/services'),
  getById:  (id)       => api.get(`/services/${id}`),
  create:   (data)     => api.post('/services', data),
  update:   (id, data) => api.put(`/services/${id}`, data),
  delete:   (id)       => api.delete(`/services/${id}`),
};
export const clientPortalAPI = {
    getDashboard:         ()    => api.get('/client/dashboard'),
    getProfile:           ()    => api.get('/client/profile'),
    getVisits:            (p)   => api.get('/client/visits', { params: p }),
    getUpcomingVisits:    ()    => api.get('/client/visits/upcoming'),
    getTodayVisits:       ()    => api.get('/client/visits/today'),
    getCaregivers:        ()    => api.get('/client/caregivers'),
    getAttendance:        ()    => api.get('/client/attendance'),
    getTodayAttendance:   ()    => api.get('/client/attendance/today'),
    getCarePlan:          ()    => api.get('/client/careplan'),
    getNotifications:     ()    => api.get('/client/notifications'),
    markNotificationRead: (id)  => api.put(`/client/notifications/${id}/read`),
};